/* eslint-disable max-len */
import { useState } from "react";
import {
  SimpleForm,
  useGetList,
  useRefresh,
  useTranslate,
  useCreate,
  useRecordContext
} from "react-admin";
import { useParams } from "react-router-dom";
import { ShowInDialogButton } from "@react-admin/ra-form-layout";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { muiColors } from "themes/colors";
import { CustomButton } from "./CustomButton";
import * as User from "../providers/user";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const titleHelper = {
  SERVICE: "add_service",
  ADDON: "add_add_on",
  FREEFORM: "add_freeform",
  DISCOUNT: "add_discount"
};

const CustomToolbar = ({
  loading,
  amount,
  whatToAdd,
  selectedArtist,
  description
}: {
  loading: boolean;
  amount: number;
  whatToAdd: "SERVICE" | "ADDON" | "FREEFORM" | "DISCOUNT" | null;
  selectedArtist: any;
  description: any;
}) => {
  const translate = useTranslate();

  return (
    <div className="flex justify-between items-center px-[16px] pb-[16px]">
      <div>
        <>
          <div className="text-[12px] text-mono-secondaryTextColor font-['Inter'] font-[400]">
            {translate("order.final_amount")}:
          </div>
          <div className="text-[16px] text-mono-primaryTextColor font-['Inter'] font-[400]">
            ${amount}
          </div>
        </>
      </div>
      <CustomButton
        type="submit"
        variant="primary"
        cyId="add-to-order-save-button"
        text={translate("ra.action.save")}
        disabled={
          loading ||
          !amount ||
          ((whatToAdd === "SERVICE" || whatToAdd === "ADDON") &&
            !selectedArtist?.id)
        }
      />
    </div>
  );
};

export default function AddToOrderModal({
  whatToAdd,
  setWhatToAdd,
  refetch,
  txItemRefetch,
  txAddonRefetch
}: {
  whatToAdd: "SERVICE" | "ADDON" | "FREEFORM" | "DISCOUNT" | null;
  setWhatToAdd: React.Dispatch<
    React.SetStateAction<"SERVICE" | "ADDON" | "FREEFORM" | "DISCOUNT" | null>
  >;
  refetch: () => Promise<any>;
  txItemRefetch: () => Promise<any>;
  txAddonRefetch: () => Promise<any>;
}) {
  const translate = useTranslate();
  const { id } = useParams<{ id: string }>();
  const { permissions } = User.useUser();
  const [create] = useCreate();
  const [selectedArtist, setSelectedArtist] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState<number | string>(0);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedAddOn, setSelectedAddOn] = useState<any>(null);
  const [description, setDescription] = useState<string>("");
  const refresh = useRefresh();
  const { amount_remaining } = useRecordContext();

  const onSubmit = () => {
    setLoading(true);
    try {
      const payload = {
        service: { id: selectedService?.id },
        whatToAdd,
        artist: selectedArtist?.id ? { id: selectedArtist.id } : null,
        price: whatToAdd === "DISCOUNT" ? -amount : amount,
        description,
        order: { id }
      };
      if (whatToAdd === "SERVICE") {
        create(
          "TxService",
          { data: payload },
          {
            onSuccess: async () => {
              closeModal();
              await refetch();
              await txItemRefetch();
            },
            onError: () => {
              setLoading(false);
            }
          }
        );
      } else if (whatToAdd === "ADDON") {
        create(
          "TxAddon",
          {
            data: {
              ...payload,
              service: null,
              addon: { id: selectedAddOn?.id }
            }
          },
          {
            onSuccess: async () => {
              closeModal();
              await refetch();
              await txAddonRefetch();
            },
            onError: () => {
              setLoading(false);
            }
          }
        );
      } else {
        if (amount === 0) {
          setLoading(false);
          throw new Error("Amount cannot be 0");
        }
        create(
          "TxItem",
          { data: payload },
          {
            onSuccess: async () => {
              closeModal();
              await refetch();
              await txItemRefetch();
            },
            onError: () => {
              setLoading(false);
            }
          }
        );
      }
    } catch (e) {
      setLoading(false);
      setError(e.message);
    }
  };

  const resetStates = () => {
    setError(null);
    setSelectedService(null);
    setSelectedArtist(null);
    setSelectedAddOn(null);
    setAmount(0);
    setDescription("");
    setLoading(false);
  };

  const closeModal = () => {
    setWhatToAdd(null);
    resetStates();
    refresh();
  };

  const { data } = useGetList("Service", {
    filter: {
      company_id: permissions?.companyId
    },
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 500 }
  });

  const { data: artistData } = useGetList("Artist", {
    filter: {
      user: {
        is: {
          joined_company_id: permissions?.companyId
        }
      },
      is_active: true
    }
  });

  const artistChoices = artistData?.map((item: any) => {
    return {
      id: item?.id,
      name: `${item?.user?.first_name} ${item?.user?.last_name}`
    };
  });

  const serviceChoices = data
    ?.filter((item) => !item.is_addon)
    ?.map((item) => {
      return {
        id: item?.id,
        name: `${item.name} - $${item?.price?.toFixed(2)}`,
        price: item?.price,
        description: item?.description
      };
    });

  const addOnChoices = data
    ?.filter((item) => item.is_addon)
    ?.map((item) => {
      return {
        id: item?.id,
        name: `${item.name} - $${item?.price?.toFixed(2)}`,
        price: item?.price,
        description: item?.description
      };
    });

  return (
    <>
      <ShowInDialogButton
        title={
          whatToAdd
            ? translate(`order.${titleHelper[whatToAdd]}`)
            : translate("order.add_to_order")
        }
        label=" "
        fullWidth
        ButtonProps={{
          value: translate("order.finish_order"),
          variant: "contained",
          onClickCapture: () => setWhatToAdd((prev) => prev),
          sx: { padding: 0, visibility: "hidden" }
        }}
        isOpen={!!whatToAdd}
        onClose={closeModal}
        close={closeModal}
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: muiColors.paper,
            borderRadius: "8px"
          },
          "& #edit-dialog-title": {
            textAlign: "center",
            textTransform: "uppercase",
            fontFamily: "Inter",
            fontSize: "14px"
          },
          "& .RaFormDialogTitle-closeButton": {
            display: "none"
          }
        }}
      >
        <SimpleForm
          onSubmit={onSubmit}
          resolver={yupResolver(validationSchema)}
          toolbar={
            <CustomToolbar
              loading={loading}
              amount={amount === "" ? 0 : (amount as number)}
              whatToAdd={whatToAdd}
              selectedArtist={selectedArtist}
              description={description}
            />
          }
          sx={{
            "& .MuiFilledInput-root": {
              borderRadius: "8px",
              border: "1px solid rgba(48, 47, 46, 0.06)"
            },
            "& .MuiFilledInput-input": {
              height: "24px",
              fontSize: "16px",
              borderRadius: "8px",
              fontFamily: "Inter",
              border: "none"
            },
            "& .MuiFilledInput-input:focus": {
              boxShadow: "none",
              borderRadius: "8px !important"
            },
            "& .MuiFilledInput-root:before": {
              border: "none !important"
            },
            "& .MuiFilledInput-root:after": {
              border: "1px solid #b8722a !important",
              top: "0px",
              borderRadius: "8px",
              transition: "none"
            },
            "& .MuiFilledInput-root.Mui-error:after": {
              border: "1px solid #EF5B1B !important"
            },
            "& .MuiFilledInput-root.Mui-disabled": {
              backgroundColor: "rgba(48, 47, 46, 0.06)"
            },
            "& .MuiTextField-root": {
              marginTop: "0px",
              marginBottom: "0px"
            },
            "& .MuiInputLabel-root": {
              fontSize: "16px",
              color: "rgba(48, 47, 46, 0.6)"
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#b8722a"
            },
            "& .MuiFormHelperText-root": {
              fontSize: "12px",
              marginTop: "2px",
              marginBottom: "2px",
              lineHeight: "14px"
            },
            "[type='number']:focus": {
              boxShadow: "none"
            }
          }}
        >
          {error && <p style={{ color: "red" }}>{error}</p>}
          {whatToAdd === "SERVICE" && (
            <>
              <FormControl fullWidth sx={{ marginTop: "16px" }}>
                <InputLabel
                  id="services-custom-select"
                  sx={{
                    fontSize: "16px",
                    color: "rgba(48, 47, 46, 0.6)",
                    fontFamily: "Inter",
                    transform: selectedService
                      ? "translate(12px, 4px) scale(0.75)"
                      : undefined,
                    "&.Mui-focused": {
                      transform: "translate(12px, 4px) scale(0.75)"
                    }
                  }}
                >
                  {translate("order.services")}
                </InputLabel>
                <Select
                  labelId="services-custom-select"
                  id="services-custom-select"
                  fullWidth
                  variant="filled"
                  value={selectedService}
                  data-cy="service-select"
                  onChange={(event) => {
                    setSelectedService(event?.target?.value);
                    setAmount(event?.target?.value?.price);
                    setDescription(event?.target?.value?.description);
                  }}
                  sx={{
                    "& .MuiFilledInput-input": {
                      paddingTop: "22px",
                      paddingBottom: "6px",
                      fontWeight: 400
                    }
                  }}
                  renderValue={(value) => {
                    return <span>{value?.name}</span>;
                  }}
                >
                  {serviceChoices?.map((item: any) => (
                    <MenuItem data-cy="service-option" value={item}>
                      {item?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ marginTop: "16px" }}>
                <InputLabel
                  id="artists-custom-select"
                  sx={{
                    fontSize: "16px",
                    color: "rgba(48, 47, 46, 0.6)",
                    fontFamily: "Inter",
                    transform: selectedArtist
                      ? "translate(12px, 4px) scale(0.75)"
                      : undefined,
                    "&.Mui-focused": {
                      transform: "translate(12px, 4px) scale(0.75)"
                    }
                  }}
                >
                  {translate("order.artist")}
                </InputLabel>
                <Select
                  labelId="artists-custom-select"
                  id="artists-custom-select"
                  fullWidth
                  data-cy="artist-select"
                  variant="filled"
                  value={selectedArtist}
                  onChange={(event) => setSelectedArtist(event?.target?.value)}
                  sx={{
                    "& .MuiFilledInput-input": {
                      paddingTop: "22px",
                      paddingBottom: "6px",
                      fontWeight: 400
                    }
                  }}
                  renderValue={(value) => {
                    return <span>{value?.name}</span>;
                  }}
                >
                  {artistChoices?.map((item: any) => (
                    <MenuItem data-cy="artist-option" value={item}>
                      {item?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          {whatToAdd === "ADDON" && (
            <>
              <FormControl fullWidth sx={{ marginTop: "16px" }}>
                <InputLabel
                  id="addons-custom-select"
                  sx={{
                    fontSize: "16px",
                    color: "rgba(48, 47, 46, 0.6)",
                    fontFamily: "Inter",
                    transform: selectedAddOn
                      ? "translate(12px, 4px) scale(0.75)"
                      : undefined,
                    "&.Mui-focused": {
                      transform: "translate(12px, 4px) scale(0.75)"
                    }
                  }}
                >
                  {translate("common.add_ons")}
                </InputLabel>
                <Select
                  labelId="addons-custom-select"
                  id="addons-custom-select"
                  fullWidth
                  data-cy="addon-select"
                  variant="filled"
                  value={selectedAddOn}
                  onChange={(event) => {
                    setSelectedAddOn(event?.target?.value);
                    setAmount(event?.target?.value?.price);
                    setDescription(event?.target?.value?.description);
                  }}
                  sx={{
                    "& .MuiFilledInput-input": {
                      paddingTop: "22px",
                      paddingBottom: "6px",
                      fontWeight: 400
                    }
                  }}
                  renderValue={(value) => {
                    return <span>{value?.name}</span>;
                  }}
                >
                  {addOnChoices?.map((item: any) => (
                    <MenuItem data-cy="addon-option" value={item}>
                      {item?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ marginTop: "16px" }}>
                <InputLabel
                  id="artists-custom-select"
                  sx={{
                    fontSize: "16px",
                    color: "rgba(48, 47, 46, 0.6)",
                    fontFamily: "Inter",
                    transform: selectedArtist
                      ? "translate(12px, 4px) scale(0.75)"
                      : undefined,
                    "&.Mui-focused": {
                      transform: "translate(12px, 4px) scale(0.75)"
                    }
                  }}
                >
                  {translate("order.artist")}
                </InputLabel>
                <Select
                  labelId="artists-custom-select"
                  id="artists-custom-select"
                  fullWidth
                  variant="filled"
                  data-cy="artist-select"
                  value={selectedArtist}
                  onChange={(event) => setSelectedArtist(event?.target?.value)}
                  sx={{
                    "& .MuiFilledInput-input": {
                      paddingTop: "22px",
                      paddingBottom: "6px",
                      fontWeight: 400
                    }
                  }}
                  renderValue={(value) => {
                    return <span>{value?.name}</span>;
                  }}
                >
                  {artistChoices?.map((item: any) => (
                    <MenuItem data-cy="artist-option" value={item}>
                      {item?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          {(whatToAdd === "FREEFORM" || whatToAdd === "DISCOUNT") && (
            <>
              <TextField
                variant="filled"
                type="number"
                label={translate("order.amount")}
                InputProps={{
                  inputProps: {
                    min: 0,
                    nullable: true,
                    "data-cy": "amount-input"
                  }
                }}
                value={amount.toString()}
                onFocus={(e) => {
                  if (e.target.value === "0") {
                    setAmount("");
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === "") {
                    setAmount(0);
                  }
                }}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (
                    value > Number(amount_remaining) &&
                    whatToAdd === "DISCOUNT"
                  ) {
                    e.target.value = amount_remaining;
                    setAmount(Number(amount_remaining));
                  } else {
                    if (e.target.value === "") {
                      setAmount("");
                    } else {
                      setAmount(value);
                    }
                  }
                }}
                fullWidth
                sx={{
                  "& .MuiFilledInput-input": {
                    paddingTop: "22px",
                    paddingBottom: "5px",
                    fontWeight: 400
                  },
                  "& .MuiFilledInput-root, .MuiFilledInput-input, .MuiFilledInput-input:focus, .MuiFilledInput-root:after":
                    {
                      borderBottomLeftRadius: "0px",
                      borderBottomRightRadius: "0px"
                    },
                  "& .MuiFormHelperText-root": {
                    display: "none"
                  }
                }}
              />
              <TextField
                variant="filled"
                label={translate("order.description")}
                data-cy="description-input"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                fullWidth
                sx={{
                  "& .MuiFilledInput-input": {
                    paddingTop: "22px",
                    paddingBottom: "5px",
                    fontWeight: 400
                  },
                  "& .MuiFilledInput-root, .MuiFilledInput-input, .MuiFilledInput-input:focus, .MuiFilledInput-root:after":
                    {
                      borderTopLeftRadius: "0px",
                      borderTopRightRadius: "0px"
                    },
                  "& .MuiFormHelperText-root": {
                    display: "none"
                  }
                }}
              />
              {whatToAdd === "FREEFORM" && (
                <FormControl fullWidth sx={{ marginTop: "16px" }}>
                  <InputLabel
                    id="artists-custom-select"
                    sx={{
                      fontSize: "16px",
                      color: "rgba(48, 47, 46, 0.6)",
                      fontFamily: "Inter",
                      transform: selectedArtist
                        ? "translate(12px, 4px) scale(0.75)"
                        : undefined,
                      "&.Mui-focused": {
                        transform: "translate(12px, 4px) scale(0.75)"
                      }
                    }}
                  >
                    {translate("order.artist")}
                  </InputLabel>
                  <Select
                    labelId="artists-custom-select"
                    id="artists-custom-select"
                    fullWidth
                    data-cy="artist-select"
                    variant="filled"
                    value={selectedArtist}
                    onChange={(event) =>
                      setSelectedArtist(event?.target?.value)
                    }
                    sx={{
                      "& .MuiFilledInput-input": {
                        paddingTop: "22px",
                        paddingBottom: "6px",
                        fontWeight: 400
                      }
                    }}
                    renderValue={(value) => {
                      return <span>{value?.name}</span>;
                    }}
                  >
                    {artistChoices?.map((item: any) => (
                      <MenuItem data-cy="artist-option" value={item}>
                        {item?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}
          <IconButton
            sx={{
              position: "absolute",
              top: "8px",
              right: "8px"
            }}
            onClick={closeModal}
          >
            <CloseIcon />
          </IconButton>
        </SimpleForm>
      </ShowInDialogButton>
    </>
  );
}

const validationSchema = yup.object().shape({});
